import { Injectable } from '@angular/core';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { ITransferData } from '@app/shared/types/entities/transfer/transfer.type';
import {
  ITaskOptions,
  IBulkUpdateDocumentsResult,
  TASK_TYPE,
  ITask,
  IChangeStatusTotalRes,
  TASK_STATUS,
} from '@app/shared/types/entities/base/base.type';
import { iif } from 'rxjs';
import { ObjectId, EntityId } from '@app/shared/types/entities/entity-id.type';
import {
  size,
  merge,
  isEqual,
  includes,
  map,
  filter,
  isNil,
  isEmpty,
  partition,
  pick,
  concat,
  get,
  head,
  isFunction,
  chain,
  reduce,
  omit,
  groupBy,
  flatMap,
} from 'lodash';
import {
  observableOf,
  rx,
  observableThrowError,
  Observable,
} from '@app/shared/rx/rx-wrapper';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { UiActions } from '@app/store/actions';
import { BaseService } from '../base';
import { fromPromise } from 'rxjs/internal/observable/fromPromise';
import { TaskService } from '../task/task.service';
import { IEntityConfig } from '@app/shared/types/entities/entity-config.type';
import { transferServiceConfig } from '../transfer/transfer.service.config';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationType } from '@app/shared/types/ui/components/notification.type';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
} from '@app/shared/constants/editor-messages';
import {
  generateBulkUpdateConfirmMessageParam,
  getAutoCompleteTransferNextStatus,
} from '../transfer/transfer.util';
import { generateProgressBarLoadingParams } from '@app/shared/utils/task.utils';
import {
  COMMON_ACTION_TYPES,
  COMMON_ACTION_TYPES_REJECT,
} from '@app/shared/types/common/common-actions';
import { CHANGE_STATUS_TOTAL_STEPS } from '../transfer/transfer-constants';
import { ACTION_OPERATE_MSG_PARAMS_MAPPING } from './transfer-action-messages.constants';
import { batchRequestMap } from '@app/shared/rx-operators';
import { I18n } from '@app/i18n/strings-keys';
import { COMMON_ACTION_VALUE_ENUM_OPTIONS } from '@app/shared/constants/common-actions';

/**
 * need config and because this extends BaseService to handle basic api req
 * need inject this to utils service to avoid conflict with transfer service
 */
@Injectable()
export class TransferBulkStatusService extends BaseService {
  public static config: IEntityConfig = transferServiceConfig;

  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(TaskService) public taskService: TaskService;

  public async processBulkUpdateStatus(
    items: ITransferData[],
    options: {
      action: string;
      needDmSync?: boolean;
      onSingleDmSync?: (item: ITransferData) => Promise<void> | void;
    },
  ): Promise<'Success' | 'Failed' | 'Cancel'> {
    if (isEmpty(items)) {
      return 'Success';
    }

    const { needDmSync } = merge({ needDmSync: false }, options);

    const totalCount = size(items);
    const isMultiple = totalCount > 1;

    let singleDmSyncId: string;

    // check and call onSingleDmSync to handle single dm sync data
    if (!isMultiple && needDmSync) {
      const needDmSyncIds = await this.getDmSyncTransferIdsObs(items, {
        isSync: true,
      }).toPromise();

      if (!isEmpty(needDmSyncIds)) {
        singleDmSyncId = head(needDmSyncIds);

        if (isFunction(options?.onSingleDmSync)) {
          await options.onSingleDmSync(head(items));

          return 'Failed';
        }
      }
    }

    const actionText = get(COMMON_ACTION_VALUE_ENUM_OPTIONS, options.action);

    const message = isMultiple
      ? this.i18nService.instant(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateHowManyTarget,
          generateBulkUpdateConfirmMessageParam({
            action: actionText,
            did: null,
            count: totalCount,
            target: I18n.Forms.TransferForm.transfersBrackets,
            i18nService: this.i18nService,
          }),
        )
      : this.i18nService.instant(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateTarget,
          {
            target: this.i18nService.instant(I18n.Forms.TransferForm.transfer),
            wantTo: this.i18nService.instant(actionText),
          },
        );

    const result = await this.uiActions.confirm({
      text: message,
    });
    if (!result.confirmed) {
      return 'Cancel';
    }

    const isAccept = isEqual(options.action, COMMON_ACTION_TYPES.Accept);

    if (isMultiple || isAccept) {
      this.uiActions.enableLoader(
        isMultiple
          ? generateProgressBarLoadingParams({ i18nService: this.i18nService })
          : null,
      );
      this.uiActions.preventLoaderCloseWhenTimeout(true);
    }

    if (isAccept) {
      this.uiActions.preventDisableLoaderAfterRequestFinish(isAccept);
    }

    const nextStatus = this.getActionNextStatus(options.action);

    const transferDifferListWithStep = this.getTransferDifferListWithStep(
      nextStatus,
      items,
    );
    const totalStepCount = head(transferDifferListWithStep)?.progressStepCount;

    const updateItemObs = observableOf(true).pipe(
      rx.mergeMap(() =>
        this.bulkUpdateStatus(
          nextStatus,
          map(items, (item) => ({
            ...item,
            need_ack: singleDmSyncId === item._id,
          })),
          {
            isSync: !isMultiple,
            noLoader: isMultiple,
          },
        ),
      ),
    );

    const multiDmSyncSaveObs = observableOf(true).pipe(
      rx.mergeMap(() =>
        this.getDmSyncTransferIdsObs(items, {
          noLoader: true,
          isSync: !isMultiple,
          stepCount: isMultiple ? totalStepCount : null,
        }).pipe(
          rx.mergeMap((dmSyncTransferIds) => {
            return this.bulkUpdateStatus(
              nextStatus,
              map(items, (transfer: ITransferData) => ({
                ...transfer,
                need_ack: dmSyncTransferIds.includes(get(transfer, '_id')),
              })),
              {
                noLoader: true,
                currentStep: CHANGE_STATUS_TOTAL_STEPS,
                stepCount: totalStepCount,
              },
            );
          }),
          rx.map((processResult) => {
            return {
              ...processResult,
              totalCount,
            };
          }),
        ),
      ),
    );

    const updateAllSuccess = await iif(
      () => isMultiple && needDmSync,
      multiDmSyncSaveObs,
      updateItemObs,
    )
      .pipe(
        rx.map((bulkUpdateResult) => {
          this.shownUpdateMessage(
            {
              action: options.action,
              isSingle: !isMultiple,
            },
            bulkUpdateResult,
          );

          return bulkUpdateResult.totalCount === bulkUpdateResult.successCount;
        }),
        rx.catchError((error: HttpErrorResponse) => {
          this.uiActions.showToasterMessage({
            type: NotificationType.ERROR,
            title: TOASTER_ERROR_MESSAGE.title,
            body: I18n.ErrorMessages.CheckInternetAndTryAgain,
          });

          return observableThrowError(error);
        }),
      )
      .toPromise();

    if (isAccept) {
      this.uiActions.preventDisableLoaderAfterRequestFinish(false);
    }

    if (isMultiple || isAccept) {
      this.uiActions.disableLoader();
      this.uiActions.preventLoaderCloseWhenTimeout(false);
    }

    return updateAllSuccess ? 'Success' : 'Failed';
  }

  public getActionNextStatus(action: string): TRANSFER_STATUS_VALUE {
    const nextStatusMapping: Record<string, TRANSFER_STATUS_VALUE> = {
      [COMMON_ACTION_TYPES.Submit]: TRANSFER_STATUS_VALUE.Pending,
      [COMMON_ACTION_TYPES.Accept]: TRANSFER_STATUS_VALUE.Completed,
      [COMMON_ACTION_TYPES.Cancel]: TRANSFER_STATUS_VALUE.Canceled,
      [COMMON_ACTION_TYPES.Reject]: TRANSFER_STATUS_VALUE.Rejected,
      [COMMON_ACTION_TYPES_REJECT]: TRANSFER_STATUS_VALUE.Rejected,
    };

    return get(nextStatusMapping, action);
  }

  public getTransferDifferListWithStep(
    nextStatus: TRANSFER_STATUS_VALUE,
    items: ITransferData[],
  ): {
    transferList: ITransferData[];
    progressCurrentStep: number;
    progressStepCount: number;
  }[] {
    const itemsWithAutoComplete: ITransferData[] = map(items, (item) => {
      return {
        ...item,
        status: isEqual(TRANSFER_STATUS_VALUE.Pending, nextStatus)
          ? getAutoCompleteTransferNextStatus(
              item.transfer_type,
              item.status,
              this.stateService,
            )
          : nextStatus,
      };
    });

    const differenceStatusList = filter(
      groupBy(itemsWithAutoComplete, (item) => item.status),
      (statusList) => !isEmpty(statusList),
    );

    /**
     * if status is same its 1, so we need to get rid of it.
     */
    const stepCountWithDifferStatus =
      CHANGE_STATUS_TOTAL_STEPS + size(differenceStatusList) - 1;

    return map(differenceStatusList, (statusList, additionStep) => ({
      transferList: statusList,
      // update status is step 2, so here is 2 as default current status step
      progressCurrentStep: 2 + additionStep,
      progressStepCount: stepCountWithDifferStatus,
    }));
  }

  public getDmSyncTransferIdsObs(
    items: ITransferData[],
    options?: ITaskOptions,
  ): Observable<EntityId[]> {
    return this.taskService.getDmSyncResult(items, options).pipe(
      rx.map((syncResult) => {
        return map(
          filter(map(syncResult.sub_tasks, 'value'), 'is_acknowledge'),
          'transfer_id',
        );
      }),
      rx.catchError((error: HttpErrorResponse) => {
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: TOASTER_ERROR_MESSAGE.title,
          body: this.i18nService.instant(
            I18n.ErrorMessages.CheckInternetAndTryAgain,
          ),
        });

        return observableThrowError(error);
      }),
    );
  }

  public bulkUpdateStatus(
    status: TRANSFER_STATUS_VALUE,
    items: ITransferData[],
    options?: ITaskOptions,
  ): Observable<
    IBulkUpdateDocumentsResult<{
      _id: ObjectId;
      status: TRANSFER_STATUS_VALUE;
    }>
  > {
    const totalCount = size(items);
    const { isSync, noLoader } = merge(
      { isSync: false, noLoader: false },
      options,
    );

    if (isEqual(totalCount, 0)) {
      return observableOf({
        totalCount,
        successCount: 0,
        updated_documents: [],
      });
    }

    if (!noLoader) this.uiActions.enableLoader();

    const transferDifferListWithStep = this.getTransferDifferListWithStep(
      status,
      items,
    );
    const itemsWithAutoComplete = flatMap(
      transferDifferListWithStep,
      'transferList',
    );

    const needBatchAcknowledgeTransferIds: string[] = includes(
      [TRANSFER_STATUS_VALUE.Canceled, TRANSFER_STATUS_VALUE.Rejected],
      status,
    )
      ? map(
          filter(
            itemsWithAutoComplete,
            (item: ITransferData) =>
              !isNil(item.updated_items) ||
              !isNil(item.unavailable_items) ||
              item.need_ack,
          ),
          '_id',
        )
      : [];

    return iif(
      () => !isEmpty(needBatchAcknowledgeTransferIds),
      this.batchAcknowledgeDocuments(needBatchAcknowledgeTransferIds),
      observableOf({}),
    ).pipe(
      rx.take(1),
      rx.switchMap(() => {
        const [needUpdateTransfers, couldUpdateStatusTransfers] = partition(
          itemsWithAutoComplete,
          (transfer) => !isEmpty(transfer.inventory_items),
        );
        if (isEmpty(needUpdateTransfers)) {
          return observableOf(itemsWithAutoComplete);
        }

        // save all items first
        return fromPromise(
          this.bulkUpdateTransfers(needUpdateTransfers, true),
        ).pipe(
          rx.map((updateResult) =>
            !isEmpty(updateResult.updated_documents)
              ? concat(
                  couldUpdateStatusTransfers,
                  updateResult.updated_documents,
                )
              : couldUpdateStatusTransfers,
          ),
        );
      }),
      rx.switchMap((transfers) => {
        const transferIds = map(transfers, '_id');

        return iif(
          () => isEmpty(transferIds),
          observableOf({
            totalCount,
            successCount: 0,
            updated_documents: [],
          }),
          batchRequestMap(
            transferDifferListWithStep,
            (statusStepList) => {
              const { transferList, progressCurrentStep, progressStepCount } =
                statusStepList;

              const toStatus = head(transferList)?.status;

              return this.taskService
                .getBulkChangeStatusResult(
                  { isSync, toStatus, transferIds: map(transferList, '_id') },
                  {
                    isSync,
                    noLoader,
                    currentStep: progressCurrentStep,
                    stepCount: progressStepCount,
                  },
                )
                .pipe(rx.map((task) => ({ ...task, toStatus })));
            },
            1,
          ).pipe(
            rx.reduce((totalTask, curTask) => concat(totalTask, curTask), []),
            rx.map(
              (
                tasks: ITask &
                  {
                    toStatus: TASK_STATUS;
                  }[],
              ) => {
                const updatedItems = reduce(
                  tasks,
                  (totalItems, task) => {
                    const updateTransferItems = chain(
                      get(task, 'sub_tasks', []),
                    )
                      .filter({ type: TASK_TYPE.UpdateTransfer })
                      .map((subTask) => {
                        const transferStatus = get(
                          subTask,
                          'value.transfer_status',
                        );

                        return {
                          _id: get(subTask, 'value.transfer_id'),
                          status: transferStatus,
                          success: isEqual(transferStatus, task.toStatus),
                        };
                      })
                      .value();

                    return concat(totalItems, updateTransferItems);
                  },
                  [],
                );

                return {
                  totalCount,
                  successCount: updatedItems.filter((item) => item.success)
                    .length,
                  updated_documents: map(updatedItems, (item) =>
                    omit(item, 'success'),
                  ),
                };
              },
            ),
          ),
        ).pipe(
          rx.catchError(() => {
            return observableOf(<IChangeStatusTotalRes>{
              successful: { total: 0, list: [] },
              failed: { total: totalCount, list: itemsWithAutoComplete },
            });
          }),
          rx.map((updateStatusResult: any) => {
            return {
              totalCount: updateStatusResult.totalCount,
              successCount: updateStatusResult.successCount,
              updated_documents: updateStatusResult.updated_documents,
            };
          }),
          rx.finalize(() => {
            if (!noLoader) {
              this.uiActions.disableLoader();
            }
          }),
        );
      }),
    );
  }

  public async bulkUpdateTransfers(
    transfers: ITransferData[],
    noLoader: boolean = false,
  ): Promise<IBulkUpdateDocumentsResult> {
    if (isEmpty(transfers)) {
      return {
        totalCount: 0,
        successCount: 0,
        updated_documents: [],
      };
    }

    if (!noLoader) {
      this.uiActions.enableLoader();
    }

    const requestFields = ['status', 'inventory_items', '_id'];

    const requestDocuments = map(transfers, (item) =>
      pick(item, requestFields),
    );

    return this.bulkUpdateEntity(requestDocuments, {})
      .pipe(
        rx.finalize(() => {
          if (!noLoader) {
            this.uiActions.disableLoader();
          }
        }),
      )
      .toPromise();
  }

  public shownUpdateMessage(
    msgOpts: {
      action: string;
      isSingle: boolean;
    },
    updateResult: IBulkUpdateDocumentsResult,
  ): void {
    const { action, isSingle } = msgOpts;

    const msgData = ACTION_OPERATE_MSG_PARAMS_MAPPING[action];

    const target = this.i18nService.instant(
      I18n.Forms.TransferForm.transfersBrackets,
    );

    const successCount = get(updateResult, 'successCount', 0);
    const failedCount = updateResult.totalCount - successCount;

    if (failedCount <= 0) {
      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: isSingle
          ? this.i18nService.instant(I18n.InfoMessages.Didsuccessfully, {
              did: this.i18nService.instant(msgData.did),
            })
          : this.i18nService.instant(
              I18n.InfoMessages.PartialOrAllsuccessfully,
              {
                target,
                did: this.i18nService.instant(msgData.lowerCasedDid),
                successCount: updateResult.totalCount,
                trailSymbol: '!',
              },
            ),
      });
    } else {
      let errorMsg = this.i18nService.instant(
        I18n.ErrorMessages.BulkOperationFailed,
        {
          failedCount,
          target,
          did: this.i18nService.instant(msgData.lowerCasedDid),
        },
      );

      if (successCount > 0) {
        errorMsg =
          this.i18nService.instant(I18n.InfoMessages.PartialOrAllsuccessfully, {
            successCount,
            target,
            did: this.i18nService.instant(msgData.lowerCasedDid),
            trailSymbol: '. ',
          }) + errorMsg;
      } else if (isSingle) {
        errorMsg = this.i18nService.instant(I18n.ErrorMessages.FailedToDo, {
          wantTo: this.i18nService.instant(msgData.wantTo),
        });
      }

      // show error msg when its not empty
      if (!isEmpty(errorMsg)) {
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: TOASTER_ERROR_MESSAGE.title,
          body: errorMsg,
        });
      }
    }
  }
}
